/**
 * Realtor Referrals API Service (Phase 6).
 *
 * Frontend client for /api/v1/realtor-referrals/*. Same envelope shape
 * as the rest of the realtor services.
 */

import { apiClient } from "@/lib/api-client";

const API_BASE = "/api/v1";

export type ReferralStatus =
	| "pending"
	| "converted"
	| "hired_designer"
	| "hired_contractor";

export interface RealtorReferral {
	id: string;
	realtorId: string;
	handoffId: string | null;
	listingId: string | null;
	buyerUserId: string;
	status: ReferralStatus;
	creditEarnedPaise: number;
	creditPaidAt: string | null;
	createdAt: string;
	updatedAt: string;
}

export interface ReferralRow {
	referral: RealtorReferral;
	listing: {
		id: string;
		headline: string;
		addressPublic: string;
		city: string;
		locality: string;
	} | null;
	handoff: {
		id: string;
		status: "sent" | "clicked" | "onboarded" | "expired";
		onboardedAt: string | null;
	} | null;
}

interface BackendErrorBody {
	message?: string;
	error?: string;
}
interface AxiosLikeError {
	response?: { data?: BackendErrorBody; status?: number };
	code?: string;
	message?: string;
}

interface ApiResult<T> {
	success: boolean;
	error?: string;
	errorCode?: string | null;
	data?: T;
}

function buildFailure<T>(error: unknown, fallback: string): ApiResult<T> {
	if (error == null) {
		return { success: false, error: fallback, errorCode: "UNKNOWN_ERROR" };
	}
	const err = error as AxiosLikeError;
	if (!err.response) {
		return {
			success: false,
			error:
				err.code === "ECONNABORTED" || err.message?.includes("timeout")
					? "Request timed out. Check your connection and try again."
					: "Couldn't reach the server. Check your connection and try again.",
			errorCode: "NETWORK_ERROR",
		};
	}
	return {
		success: false,
		error: err.response.data?.message ?? fallback,
		errorCode: err.response.data?.error ?? null,
	};
}

export async function listMyReferrals(): Promise<ApiResult<ReferralRow[]>> {
	try {
		const response = await apiClient.get<{
			data: { referrals: ReferralRow[] };
		}>(`${API_BASE}/realtor-referrals/me`);
		return { success: true, data: response.data.data.referrals };
	} catch (error: unknown) {
		return buildFailure<ReferralRow[]>(error, "Failed to load referrals");
	}
}

/**
 * Trigger a CSV download. We use a blob response + manual anchor click
 * instead of a plain `<a download>` tag because apiClient attaches the
 * Authorization header — without it the request 401s.
 */
export async function downloadCrmCsv(): Promise<ApiResult<null>> {
	let url: string | null = null;
	let link: HTMLAnchorElement | null = null;
	try {
		const response = await apiClient.get(
			`${API_BASE}/realtor-referrals/me/export.csv`,
			{ responseType: "blob" },
		);
		const blob = new Blob([response.data as BlobPart], {
			type: "text/csv;charset=utf-8",
		});
		url = window.URL.createObjectURL(blob);
		link = document.createElement("a");
		link.href = url;
		const filename = parseFilename(
			(response as { headers?: Record<string, string> }).headers?.[
				"content-disposition"
			],
		);
		link.download = filename ?? `finishd-realtor-${Date.now()}.csv`;
		document.body.appendChild(link);
		link.click();
		return { success: true, data: null };
	} catch (error: unknown) {
		return buildFailure<null>(error, "Couldn't export CSV");
	} finally {
		// Cleanup must run whether the click succeeded or threw — without
		// this, a successful download could leave the blob URL alive (and
		// a failure mid-cleanup would surface as an error toast for what
		// was actually a successful download).
		try {
			if (link?.parentNode) link.parentNode.removeChild(link);
			if (url) window.URL.revokeObjectURL(url);
		} catch {
			// Cleanup failures are non-actionable for the user.
		}
	}
}

function parseFilename(header: string | undefined): string | null {
	if (!header) return null;
	const match = /filename="?([^";]+)"?/i.exec(header);
	return match ? match[1] : null;
}
